/**
 * FacebookChannelSetup.jsx — COM-01 Facebook Messenger Mock Channel Setup
 *
 * Mirrors the WhatsAppChannelSetup pattern. Provides:
 *   - Channel overview with lifecycle status
 *   - Configuration form (Page ID, Page Name, Org Scope)
 *   - Validation check (with mock mode notice)
 *   - Activation gating (blocked in mock mode — COM-01 constraint)
 *
 * COM-01 contract:
 *   - Provider mode is ALWAYS "MOCK" in this phase
 *   - SIMULATED badge shown for all transport states
 *   - The "Activate" button is shown but disabled with clear mock notice
 *   - Messages NEVER reach real Facebook recipients in this mode
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Globe, AlertTriangle, CheckCircle2, XCircle, Info, Settings, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STEPS = ["UNCONFIGURED", "CONFIGURE", "VALIDATE", "ACTIVE"];

function StepIndicator({ status }) {
  const steps = [
    { id: "CONFIGURE", label: "Configure" },
    { id: "VALIDATE",  label: "Validate"  },
    { id: "ACTIVE",    label: "Activate"  },
  ];
  const currentIdx = steps.findIndex(s => s.id === status);

  return (
    <div className="flex items-center gap-2">
      {steps.map((step, i) => {
        const done = STATUS_STEPS.indexOf(status) > STATUS_STEPS.indexOf(step.id);
        const active = step.id === status || (status === "UNCONFIGURED" && i === 0);
        return (
          <div key={step.id} className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{
                  background: done ? "#166534" : active ? "var(--brand-gold)" : "#E5E0D8",
                  color: done || active ? "#FFFFFF" : "#78716C",
                }}
              >
                {done ? "✓" : i + 1}
              </div>
              <span className="text-xs font-medium" style={{ color: done ? "#166534" : active ? "#1C1917" : "#78716C" }}>
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className="w-6 h-px" style={{ background: done ? "#166534" : "#E5E0D8" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function FacebookChannelSetup() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationResult, setValidationResult] = useState(null);

  // Form state
  const [pageId, setPageId] = useState("");
  const [pageName, setPageName] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    axios.get(`${API}/comms/facebook/channel-config`, { withCredentials: true })
      .then(r => {
        setConfig(r.data);
        setPageId(r.data.page_id || "");
        setPageName(r.data.page_name || "");
        setNotes(r.data.notes || "");
      })
      .catch(e => setError(e?.response?.data?.detail || "Failed to load Facebook config."))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(""); setSuccess("");
    try {
      const { data } = await axios.put(
        `${API}/comms/facebook/channel-config`,
        { page_id: pageId, page_name: pageName, notes },
        { withCredentials: true }
      );
      setConfig(data);
      setSuccess("Facebook channel configuration saved.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setError(""); setSuccess("");
    try {
      const { data } = await axios.post(
        `${API}/comms/facebook/channel-config/validate`,
        {},
        { withCredentials: true }
      );
      setValidationResult(data);
      setConfig(prev => prev ? { ...prev, status: data.status } : prev);
      if (data.status === "VALIDATE") {
        setSuccess("Configuration validated. Ready for production activation when live credentials are provided.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Validation failed.");
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--brand-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  const status = config?.status || "UNCONFIGURED";

  return (
    <div className="space-y-5" data-testid="facebook-channel-setup">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#1877F215" }}>
            <Globe className="w-5 h-5" style={{ color: "#1877F2" }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: "#1C1917" }}>Facebook Messenger</h3>
            <p className="text-xs" style={{ color: "#78716C" }}>Receive and respond to Facebook Page messages</p>
          </div>
        </div>
        <span
          data-testid="fb-mock-badge"
          className="text-[9px] font-bold px-2 py-1 rounded-full flex-shrink-0"
          style={{ background: "#FEF9C3", color: "#92400E", border: "1px solid #FDE047" }}
        >
          MOCK · SIMULATED
        </span>
      </div>

      {/* Step indicator */}
      <StepIndicator status={status} />

      {/* Mock mode notice */}
      <div
        className="flex gap-2 p-3 rounded-xl text-xs"
        style={{ background: "#FEF9C3", border: "1px solid #FDE047" }}
        data-testid="fb-mock-notice"
      >
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#92400E" }} />
        <div style={{ color: "#92400E" }}>
          <strong>MOCK Mode (COM-01):</strong> Facebook Messenger is in simulated mode. 
          Messages are stored in the mock outbox and never sent to real Facebook recipients.
          To go live, configure a Meta App with a Page Access Token, webhook verify token, 
          and complete Facebook App Review.
        </div>
      </div>

      {/* Configuration form */}
      <div
        className="p-4 rounded-xl space-y-4"
        style={{ background: "#FFFFFF", border: "1px solid var(--brand-border)" }}
      >
        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: "#78716C" }}>
          Channel Configuration
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "#57534E" }}>
              Facebook Page ID <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <input
              data-testid="fb-page-id-input"
              type="text"
              value={pageId}
              onChange={e => setPageId(e.target.value)}
              placeholder="e.g. 123456789012345"
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
              style={{ border: "1px solid var(--brand-border)", color: "#1C1917" }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "#57534E" }}>
              Page Name <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <input
              data-testid="fb-page-name-input"
              type="text"
              value={pageName}
              onChange={e => setPageName(e.target.value)}
              placeholder="e.g. Prashasta Events"
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
              style={{ border: "1px solid var(--brand-border)", color: "#1C1917" }}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: "#57534E" }}>Notes (optional)</label>
          <textarea
            data-testid="fb-notes-input"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Internal notes about this channel setup..."
            className="w-full px-3 py-2 rounded-lg text-sm border outline-none resize-none"
            style={{ border: "1px solid var(--brand-border)", color: "#1C1917" }}
          />
        </div>

        {error && (
          <div className="flex gap-1.5 text-xs" style={{ color: "#991B1B" }}>
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {error}
          </div>
        )}
        {success && (
          <div className="flex gap-1.5 text-xs" style={{ color: "#166534" }}>
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> {success}
          </div>
        )}

        <div className="flex gap-2">
          <button
            data-testid="fb-save-btn"
            onClick={handleSave}
            disabled={saving || !pageId || !pageName}
            className="px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 transition-colors"
            style={{ background: "var(--brand-gold)", color: "#1C1917" }}
          >
            {saving ? "Saving…" : "Save Configuration"}
          </button>
          {status === "CONFIGURE" && (
            <button
              data-testid="fb-validate-btn"
              onClick={handleValidate}
              disabled={validating || !pageId || !pageName}
              className="px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 transition-colors"
              style={{ background: "#F4F0EA", color: "#57534E" }}
            >
              {validating ? "Validating…" : "Validate"}
            </button>
          )}
        </div>
      </div>

      {/* Validation results */}
      {validationResult && (
        <div
          className="p-4 rounded-xl space-y-2"
          style={{ background: "#F0FDF4", border: "1px solid #86EFAC" }}
          data-testid="fb-validation-results"
        >
          <p className="text-xs font-bold" style={{ color: "#166534" }}>Validation Results</p>
          {Object.entries(validationResult.checks || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-xs">
              {v
                ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#166534" }} />
                : <XCircle className="w-3.5 h-3.5" style={{ color: "#9CA3AF" }} />
              }
              <span style={{ color: v ? "#166534" : "#6B7280" }}>{k.replace(/_/g, " ")}</span>
            </div>
          ))}
          {validationResult.activation_blocked_reason && (
            <p className="text-xs mt-2" style={{ color: "#92400E" }}>
              <strong>Activation blocked:</strong> {validationResult.activation_blocked_reason}
            </p>
          )}
        </div>
      )}

      {/* Activation gated notice */}
      <div
        className="p-3 rounded-xl text-xs"
        style={{ background: "#F4F0EA", border: "1px solid var(--brand-border)" }}
        data-testid="fb-activation-gate"
      >
        <strong style={{ color: "#1C1917" }}>Production Activation Requirements:</strong>
        <ul className="mt-1 space-y-0.5" style={{ color: "#57534E" }}>
          <li>• Create a Meta App at developers.facebook.com</li>
          <li>• Subscribe to "messages" and "messaging_postbacks" webhooks</li>
          <li>• Generate a Page Access Token (never page_access_token in frontend)</li>
          <li>• Pass Facebook App Review for Page Messaging permission</li>
          <li>• Set page_access_token and webhook_verify_token in backend environment</li>
        </ul>
      </div>
    </div>
  );
}
